import { MENU_ITEM_SEPARATOR } from 'ui/menu/menu';

import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import { getEnrichmentForObjecttypeId } from 'cadenza/workbook/api/enrichment-api';
import { getStore } from 'cadenza/store/cadenza-store';
import { logger } from 'cadenza/utils/logging';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import { createEnrichmentActions } from 'cadenza/workbook/workbook-panel/databrowser/data-browser-enrichment-context-actions';
import i18n from 'cadenza/workbook/workbook-panel/databrowser/enrichment-details-context-menu-utilities.properties';
import {
  getAttributeById,
  getEnrichmentByAttributeId,
  getObjecttypes,
  getWorkbookNameAndHash,
  workbookRepositoryHasCmdb,
} from 'cadenza/workbook/workbook.state';

export async function getEnrichmentAttributeActions(attribute: WorkbookAttributeDto) {
  const { isMissingEnrichedObjecttype, enrichment } = await getEnrichment(attribute);
  if (!enrichment || (!isMissingEnrichedObjecttype && enrichment.grouped) || isMissingEnrichedObjecttype) {
    return [];
  }
  return createEnrichmentDetailsMenu(enrichment, isMissingEnrichedObjecttype);
}
export function enrichmentActionsAvailable() {
  return workbookRepositoryHasCmdb(getStore().getState());
}

export async function getEnrichmentObjecttypeActions(objecttype: Objecttype) {
  if (!enrichmentActionsAvailable()) {
    return [];
  }

  const attributeIdOfAttributeWithEnrichedObjecttypeId = objecttype.attributes?.find((attributeId) => {
    const attribute = getAttributeById(getStore().getState(), attributeId);
    return attribute.enrichedObjecttypeId;
  });
  if (!attributeIdOfAttributeWithEnrichedObjecttypeId) {
    return [];
  }
  if (attributeIdOfAttributeWithEnrichedObjecttypeId) {
    const objecttypesAvailableInTheWorkbook = getObjecttypes(getStore().getState());
    const attributeWithEnrichedObjecttypeId = getAttributeById(
      getStore().getState(),
      attributeIdOfAttributeWithEnrichedObjecttypeId,
    );
    const enrichedObjecttype = objecttypesAvailableInTheWorkbook.find(
      (objecttype) => objecttype.id === attributeWithEnrichedObjecttypeId.enrichedObjecttypeId,
    );
    const isMissingEnrichedObjecttype = !enrichedObjecttype;
    if (isMissingEnrichedObjecttype) {
      try {
        const enrichment = await getEnrichmentForObjecttypeId(
          getWorkbookNameAndHash(getStore().getState()),
          attributeWithEnrichedObjecttypeId.objecttypeId,
        );
        return [...createEnrichmentDetailsMenu(enrichment, isMissingEnrichedObjecttype), MENU_ITEM_SEPARATOR];
      } catch (e) {
        logger.warn(
          'The attribute with the Id [' +
            attributeIdOfAttributeWithEnrichedObjecttypeId +
            '] indicates an available enrichment, but there is none',
          e,
        );
      }
    }
  }
  return [];
}

async function getEnrichment(attribute: WorkbookAttributeDto) {
  const enrichmentFromStore = getEnrichmentByAttributeId(getStore().getState(), attribute.id as AttributeId);
  let enrichment = enrichmentFromStore;
  if (!enrichment && attribute.enrichedObjecttypeId) {
    // if the enrichment was added without the objecttype it is based on, the enrichment
    // information must be fetched separately. The grouping flag is ignored in this case.
    try {
      enrichment = await getEnrichmentForObjecttypeId(
        getWorkbookNameAndHash(getStore().getState()),
        attribute.objecttypeId,
      );
    } catch (error) {
      logger.error(`Unable to locate enrichment for objecttype id ${attribute.objecttypeId}`, error);
    }
  }
  return {
    enrichment,
    isMissingEnrichedObjecttype: enrichment != null && enrichment !== enrichmentFromStore,
  };
}

function createEnrichmentDetailsMenu(enrichment: EnrichmentDto, isMissingEnrichedObjecttype = false) {
  const subMenu = () => createEnrichmentActions({ enrichment, isMissingEnrichedObjecttype });
  return [
    {
      label: i18n('attribute.action.enrichment.details'),
      subMenu,
    },
  ];
}
